
import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import BrandsCarousel from '../components/BrandsCarousel';
import SectionDivider from '../components/SectionDivider';
import HeroCarousel from '../components/HeroCarousel';
import ServicesSection from '../components/ServicesSection';
import WhyChooseUs from '../components/WhyChooseUs';
import TestimonialsSection from '../components/TestimonialsSection';
import FAQSection from '../components/FAQSection';
import QuickQuoteCalculator from '../components/QuickQuoteCalculator';
import OrcamentoForm from '../components/OrcamentoForm';
import ConsultaOrcamentoForm from '../components/ConsultaOrcamentoForm';
import NewsletterForm from '../components/NewsletterForm';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AbrirChamadoForm from '../components/AbrirChamadoForm';
import { SITE_INFO, getGoogleMapsDirectionsUrl, getWhatsAppUrl } from '../content/site-info';


const Home = () => {
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_INFO.legalName,
    url: '/',
    telephone: SITE_INFO.phone.display,
    email: SITE_INFO.supportEmail,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE_INFO.address.street,
      addressLocality: SITE_INFO.address.city,
      addressRegion: SITE_INFO.address.state,
      postalCode: SITE_INFO.address.postalCode,
      addressCountry: SITE_INFO.address.country,
    },
    areaServed: SITE_INFO.cityRegion,
  };

  return (
    <>
      <Helmet>
        <title>Assistência Técnica em {SITE_INFO.cityRegion} | Notebook, PC, TV e Mais | {SITE_INFO.brandName}</title>
        <meta
          name="description"
          content={`Reparo de notebook, PC, TV, micro-ondas e no-break em ${SITE_INFO.cityRegion}. Orçamento gratuito sem compromisso, garantia e atendimento via WhatsApp.`}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>

      <div className="surface-page min-h-screen font-sans text-gray-800 dark:text-gray-200">
        {/* HERO */}
        <motion.section
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="section-padding text-center"
        >
          <div className="container-responsive">
            {/* Título - Responsivo para todos os breakpoints */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl xs:text-3xl xs2:text-4xl xs3:text-4xl xs4:text-5xl 
                       sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 xs:mb-4 
                       sm:mb-6 text-gray-900 dark:text-gray-100"
            >
              <span className="text-gradient">
                Assistência Técnica Especializada
              </span>
            </motion.h1>

            {/* Subtítulo - Responsivo para todos os breakpoints */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-sm xs:text-base xs2:text-lg xs3:text-lg xs4:text-xl 
                       sm:text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-6 xs:mb-8 sm:mb-10 
                       md:mb-12 max-w-3xl mx-auto px-2 xs:px-4"
            >
              Em reparos de Notebook, PC, TV, Microondas e No-breaks com qualidade e garantia!
            </motion.p>

            {/* CTAs - Botões de ação destacados */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col xs4:flex-row gap-3 xs:gap-4 sm:gap-5 
                         justify-center items-center mb-6 xs:mb-8 sm:mb-10 md:mb-12 
                         px-4"
            >
              <motion.a
                href="#formularios-combinados"
                className="btn-primary text-sm xs:text-base sm:text-lg px-6 xs:px-8 
                         sm:px-10 py-3 xs:py-3.5 sm:py-4 w-full xs4:w-auto min-w-[200px] 
                          text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <span className="mr-2">✨</span>
                Solicitar Orçamento
              </motion.a>
              <motion.a
                href={getWhatsAppUrl('Olá! Quero solicitar um orçamento.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm xs:text-base sm:text-lg px-6 xs:px-8 
                         sm:px-10 py-3 xs:py-3.5 sm:py-4 w-full xs4:w-auto min-w-[200px] 
                          text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <span className="mr-2">💬</span>
                Falar no WhatsApp
              </motion.a>
            </motion.div>

            {/* Carousel - Responsivo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4 xs:mt-6 sm:mt-8 md:mt-12"
            >
              <HeroCarousel />
            </motion.div>
          </div>
        </motion.section>

        <SectionDivider fill="fill-white dark:fill-gray-900" />
        <BrandsCarousel />
        <SectionDivider fill="fill-gray-50 dark:fill-gray-950" flip />
        <ServicesSection />
        <SectionDivider fill="fill-white dark:fill-gray-900" />
        <WhyChooseUs />
        <SectionDivider fill="fill-gray-50 dark:fill-gray-950" flip />
        <TestimonialsSection />
        <SectionDivider fill="fill-white dark:fill-gray-900" />
        <QuickQuoteCalculator />
        <FAQSection />

        {/* FORMULÁRIOS */}
        <section
          id="formularios-combinados"
          className="surface-section py-12 px-4 sm:px-6 md:px-8 lg:px-12 shadow-inner dark:shadow-none overflow-hidden"
        >
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <OrcamentoForm />
            <ConsultaOrcamentoForm />
            <AbrirChamadoForm />
            <NewsletterForm />
          </div>
        </section>

        {/* LOCALIZAÇÃO */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="container-responsive mb-12 xs:mb-16 sm:mb-20 md:mb-24"
        >
          <div className="card-modern shadow-large border border-gray-200 dark:border-gray-800 
                        hover:shadow-large transition-shadow duration-300">
            {/* Título */}
            <motion.h2 
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-semibold 
                        text-primary-700 mb-4 xs:mb-5 sm:mb-6 text-center"
            >
              Nossa Localização
            </motion.h2>

            {/* Informações de endereço */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-4 xs:mb-5 sm:mb-6 p-4 xs:p-5 bg-gradient-to-r from-primary-50 
                        to-primary-100 rounded-lg xs:rounded-xl border border-primary-200"
            >
              <div className="flex flex-col sm:flex-row gap-3 xs:gap-4 items-start sm:items-center 
                            justify-center sm:justify-between">
                <div className="flex items-center gap-2 xs:gap-3">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-8 h-8 xs:w-10 xs:h-10 flex items-center justify-center 
                              bg-primary-600 text-white rounded-lg"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="w-4 h-4 xs:w-5 xs:h-5" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </motion.div>
                  <div>
                    <p className="text-xs xs:text-sm sm:text-base font-semibold text-gray-800">
                      Estrada da Várzea, 25 - Qd.01
                    </p>
                    <p className="text-xs xs:text-sm text-gray-600">
                      Vila Santa Cruz, Duque de Caxias - RJ, 25260-160
                    </p>
                  </div>
                </div>
                <motion.a
                  href={getGoogleMapsDirectionsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-xs xs:text-sm px-4 py-2 flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-4 h-4" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span>Como chegar</span>
                </motion.a>
              </div>
            </motion.div>

            {/* Mapa */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="w-full h-48 xs:h-56 xs2:h-64 xs3:h-72 xs4:h-80 sm:h-80 
                        md:h-96 lg:h-[28rem] xl:h-[32rem] rounded-lg xs:rounded-xl 
                        overflow-hidden ring-2 ring-gray-200 hover:ring-primary-300 
                        transition-all duration-300"
            >
              <iframe
                title="Mapa Localização"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1841.1321995131325!2d-43.27361084618606!3d-22.643928646716088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x99733f97036c69%3A0x421d964cd3fcea05!2sEstr.%20da%20V%C3%A1rzea%2C%2025%20-%20Qd.01%20-%20Vila%20Santa%20Cruz%2C%20Duque%20de%20Caxias%20-%20RJ%2C%2025260-160!5e0!3m2!1spt-BR!2sbr!4v1754854625010!5m2!1spt-BR!2sbr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
            </motion.div>
          </div>
        </motion.section>

        {/* BOTÃO WHATSAPP - Responsivo para todos os breakpoints */}
        <motion.a
          href={getWhatsAppUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="fab-whatsapp fixed bottom-3 right-3 xs:bottom-4 xs:right-4 xs2:bottom-5 xs2:right-5 
                   sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 
                   bg-gradient-to-r from-green-500 to-green-600 
                   hover:from-green-600 hover:to-green-700 
                   text-white p-3 xs:p-3 sm:p-4 md:p-4 
                   rounded-full shadow-lg hover:shadow-green-500/50 
                   transition-all duration-300 z-50 
                   max-w-[calc(100vw-1.5rem)] xs:max-w-[calc(100vw-2rem)]
                   focus:outline-none focus:ring-4 focus:ring-green-500/50
                   group"
          aria-label="Fale conosco pelo WhatsApp"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            delay: 1,
            type: "spring",
            stiffness: 200,
            damping: 15
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Animação de pulse */}
          <motion.div
            className="absolute inset-0 rounded-full bg-green-500"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="w-6 h-6 sm:w-7 sm:h-7 relative z-10" 
            fill="white" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M20.52 3.48A11.93 11.93 0 0012 0C5.37 0 .01 5.37.01 12c0 2.11.55 4.17 1.6 6L0 24l6.17-1.61a11.94 11.94 0 005.83 1.49c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52zM12 22c-1.8 0-3.56-.47-5.11-1.36l-.37-.22-3.66.96.98-3.57-.24-.37A9.94 9.94 0 012.01 12C2.01 6.48 6.48 2 12 2s9.99 4.48 9.99 10S17.52 22 12 22zm5.27-7.4c-.29-.15-1.7-.84-1.96-.94-.26-.1-.45-.15-.64.15-.19.29-.74.94-.91 1.13-.17.19-.34.21-.63.07-.29-.15-1.23-.45-2.34-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.64-1.54-.88-2.11-.23-.56-.47-.48-.64-.48h-.55c-.19 0-.48.07-.73.36-.26.29-1 1-1 2.43s1.02 2.82 1.16 3.01c.14.19 2.01 3.07 4.87 4.3.68.29 1.21.46 1.62.59.68.22 1.3.19 1.79.12.55-.08 1.7-.7 1.94-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.34z" />
          </svg>
          
          {/* Tooltip */}
          <span className="absolute bottom-full right-0 mb-3 px-3 py-1.5 
                         bg-gray-900 text-white text-xs xs:text-sm rounded-lg whitespace-nowrap
                         opacity-0 group-hover:opacity-100 transition-opacity duration-300
                         pointer-events-none shadow-lg">
            Fale conosco!
            <span className="absolute top-full right-4 w-0 h-0 border-l-4 
                           border-r-4 border-t-4 border-transparent 
                           border-t-gray-900"></span>
          </span>
        </motion.a>

        <ToastContainer position="bottom-right" autoClose={5000} theme="colored" />
      </div>
    </>
  );
};

export default Home;
